// animationMixer.js
import { log } from '../utils/logging.js';

import {
  AnimationMixer,
  AnimationUtils,
  PropertyBinding,
  Raycaster,
  Vector2,
  Box3,
  Vector3,
  LoopOnce,
  LoopRepeat,
  LoopPingPong,
  DoubleSide
} from 'three';

const FPS = 24;
const ONCE = '_once';
const LOOP = '_loop';
const PINGPONG = '_pingpong';
const ONCLICK = '_onclick';
const DEBUG_ONCLICK = true;

if (DEBUG_ONCLICK) {
  log('[onclick] animationMixer module loaded');
}

const TRIGGER_CLIPS = new Set(['open_hatch_trigger', 'close_hatch_trigger']);

// lowercase + trim
function normalizeClipName(name) {
  if (!name) return '';
  return name.toLowerCase().trim();
}

// Extract start frame from name, e.g. "_start_10" => 10
function parseStartFrame(name) {
  const match = name.match(/_start_?(\d+)/);
  return match ? parseFloat(match[1]) : null;
}

// Extract delay from name, e.g. "_delay_15.
function parseDelay(name) {
  const match = name.match(/_delay_?(\d+)/);
  return match ? parseFloat(match[1]) / FPS : null;
}

function getLoopMode(name) {
  const lower = (name || '').toLowerCase();
  if (lower.includes(ONCLICK)) return LoopOnce;
  if (lower.includes(ONCE)) return LoopOnce;
  if (lower.includes(PINGPONG)) return LoopPingPong;
  if (lower.includes(LOOP)) return LoopRepeat;
  return LoopRepeat;
}

function shouldAutoplay(name) {
  const normalized = normalizeClipName(name);
  // Don't autoplay trigger clips
  if (TRIGGER_CLIPS.has(normalized) || normalized.includes('_trigger')) {
    return false;
  }
  if (normalized.includes(ONCLICK)) {
    // Allow autoplay once if explicitly tagged with _once + _onclick
    return normalized.includes(ONCE);
  }
  // Everything else SHOULD autoplay (including motion path clips)
  return true;
}

function isOnClickClip(name) {
  const lower = (name || '').toLowerCase();
  return lower.includes(ONCLICK);
}

function trimClip(clip, startFrame) {
  if (!clip || !startFrame || startFrame <= 0) return clip;

  const start = Math.floor(startFrame);
  const end = Math.max(start + 1, Math.round(clip.duration * FPS));

  if (start >= end - 1) return clip;

  return AnimationUtils.subclip(clip, `${clip.name}__trimmed`, start, end, FPS);
}

function getTrackNodeNames(clip) {
  const names = new Set();
  if (!clip?.tracks?.length) return names;
  clip.tracks.forEach(track => {
    if (!track?.name || typeof track.name !== 'string') return;
    let nodeName = null;
    const parser = PropertyBinding?.parseTrackName;
    if (typeof parser === 'function') {
      try {
        const parsed = parser(track.name);
        nodeName = parsed?.nodeName || parsed?.objectName || null;
      } catch {
        nodeName = null;
      }
    }
    if (!nodeName) {
      nodeName = track.name.split('.')[0];
    }
    if (nodeName) names.add(nodeName.toLowerCase());
  });
  return names;
}

function armOnClickAction(action) {
  if (!action) return;
  action.reset();
  action.setLoop(LoopOnce, 1);
  action.clampWhenFinished = false;
  action.enabled = true;
  action.weight = 1;
  action.setEffectiveWeight?.(1);
  action.setEffectiveTimeScale?.(1);
  action.play();
  action.paused = true;
  action.time = 0;
}

function playOnClickAction(action) {
  if (!action) return;
  if (action.isRunning?.() && !action.paused) return;
  action.reset();
  action.setLoop(LoopOnce, 1);
  action.clampWhenFinished = false;
  action.enabled = true;
  action.paused = false;
  action.weight = 1;
  action.setEffectiveWeight?.(1);
  action.setEffectiveTimeScale?.(1);
  action.time = 0;
  action.play();
}

function collectOnClickTargets(root, nodeNames = new Set()) {
  const targets = [];
  if (!root || nodeNames.size === 0) return targets;
  root.traverse(obj => {
    if (!obj?.isMesh && !obj?.isSkinnedMesh) return;
    let current = obj;
    let match = false;
    while (current) {
      const name = current?.name?.toLowerCase?.();
      if (name && nodeNames.has(name)) {
        match = true;
        break;
      }
      if (current === root) break;
      current = current.parent;
    }
    if (!match && obj.isSkinnedMesh && obj.skeleton?.bones?.length) {
      match = obj.skeleton.bones.some(bone => {
        let current = bone;
        while (current) {
          const boneName = current?.name?.toLowerCase?.();
          if (boneName && nodeNames.has(boneName)) return true;
          current = current.parent;
        }
        return false;
      });
    }
    if (match) targets.push(obj);
  });
  return targets;
}

function resolveOnClickActions(hit, root, nodeActionMap, allOnClickActions, buttonNames) {
  const actions = new Set();
  if (!hit || !nodeActionMap) return actions;
  const hitName = hit?.name?.toLowerCase?.() || '';
  if (hitName && buttonNames?.some(name => hitName.includes(name))) {
    allOnClickActions?.forEach(action => actions.add(action));
    return actions;
  }
  let current = hit;
  while (current) {
    const name = current?.name?.toLowerCase?.();
    if (name) {
      const set = nodeActionMap.get(name);
      if (set) set.forEach(action => actions.add(action));
    }
    if (current === root) break;
    current = current.parent;
  }
  if (hit.isSkinnedMesh && hit.skeleton?.bones?.length) {
    hit.skeleton.bones.forEach(bone => {
      let current = bone;
      while (current) {
        const boneName = current?.name?.toLowerCase?.();
        if (boneName) {
          const set = nodeActionMap.get(boneName);
          if (set) set.forEach(action => actions.add(action));
        }
        current = current.parent;
      }
    });
  }
  return actions;
}

function prepareClickTargetMesh(mesh) {
  if (!mesh?.isMesh && !mesh?.isSkinnedMesh) return;
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  materials.forEach(mat => {
    if (!mat) return;
    mat.transparent = true;
    mat.opacity = 0;
    if ('colorWrite' in mat) mat.colorWrite = false;
    mat.depthWrite = false;
    mat.side = DoubleSide;
    mat.needsUpdate = true;
  });
  mesh.visible = true;
}

function collectNamedClickTargets(root, names = []) {
  const targets = [];
  if (!root || !names.length) return targets;
  const needles = names.map(n => n.toLowerCase());
  root.traverse(obj => {
    if (!obj?.isMesh && !obj?.isSkinnedMesh) return;
    const name = obj?.name?.toLowerCase?.();
    if (!name) return;
    if (needles.some(needle => name.includes(needle))) {
      prepareClickTargetMesh(obj);
      targets.push(obj);
    }
  });
  return targets;
}

// For debugging: log all animation clip names and their normalized forms
function logAllNames(gltf) {
  console.group('🔍 GLB Animation Names');
  gltf.animations.forEach((clip, i) => {
    console.debug(`[${i}] "${clip.name}"`, {
      hasDot: clip.name.includes('.'),
      normalized: normalizeClipName(clip.name)
    });
  });
  console.groupEnd();
}

const INTRO_SKIP_SECONDS = 12;

export function shouldSkipIntro(name) {
  if (!name) return false;
  try {
    return sessionStorage.getItem(`bubar.introShown.${name}`) === '1';
  } catch {
    return false;
  }
}

export function createAnimationMixer(
  gltf,
  { camera = null, domElement = null, sceneName = null } = {}
) {
  if (DEBUG_ONCLICK) {
    log('[onclick] createAnimationMixer', {
      hasScene: Boolean(gltf?.scene),
      animations: gltf?.animations?.length || 0,
      hasCamera: Boolean(camera),
      hasDomElement: Boolean(domElement)
    });
  }
  if (!gltf?.scene || !gltf?.animations?.length) {
    return { mixer: null, actions: {}, update: () => {}, dispose: () => {} };
  }

  // logAllNames(gltf)

  const skipIntro = shouldSkipIntro(sceneName);
  const mixer = new AnimationMixer(gltf.scene);
  const actions = {};
  const onceActions = new Set();
  const clickOnceActions = new Set();
  const onClickNodeNames = new Set();
  const onClickNodeActions = new Map();
  const onClickActions = new Set();
  const buttonNames = ['button'];

  if (DEBUG_ONCLICK) {
    log(
      '[onclick] all clips:',
      (gltf.animations || []).map(c => c?.name || '(unnamed)')
    );
  }
  for (const clip of gltf.animations) {
    const name = normalizeClipName(clip.name);
    const isOnClick = isOnClickClip(clip.name);
    const startFrame = parseStartFrame(clip.name);
    const delay = parseDelay(clip.name);
    const loopMode = getLoopMode(clip.name);

    const playableClip = trimClip(clip, startFrame);

    const action = mixer.clipAction(playableClip);
    action.reset();
    action.setLoop(loopMode, loopMode === LoopOnce ? 1 : Infinity);

    if (loopMode === LoopOnce) {
      if (isOnClick) {
        clickOnceActions.add(action);
        onClickActions.add(action);
      } else {
        action.clampWhenFinished = true;
        onceActions.add(action);
      }
    }

    actions[name] = action;

    if (isOnClick) {
      const nodeNames = getTrackNodeNames(playableClip);
      nodeNames.forEach(nodeName => {
        onClickNodeNames.add(nodeName);
        let set = onClickNodeActions.get(nodeName);
        if (!set) {
          set = new Set();
          onClickNodeActions.set(nodeName, set);
        }
        set.add(action);
      });
    }

    const autoplay = shouldAutoplay(clip.name);

    if (autoplay) {
      if (delay > 0) action.startAt(delay);
      action.play();

      if (skipIntro && !TRIGGER_CLIPS.has(name) && !name.includes('_trigger')) {
        const skipTo = Math.min(INTRO_SKIP_SECONDS, playableClip.duration - 0.001);
        action.time = skipTo;
        mixer.update(0);
      }
    } else if (isOnClick) {
      armOnClickAction(action);
    } else {
      action.enabled = false;
      action.paused = true;
      action.weight = 0;
    }

    // console.debug('[animationMixer]', name, {
    //   duration: playableClip.duration,
    //   loopMode: loopMode === LoopOnce ? 'once' : loopMode === LoopPingPong ? 'pingpong' : 'loop',
    //   autoplay,
    // })
  }

  let disposeOnClick = () => {};
  if (onClickNodeNames.size > 0 && camera && domElement) {
    const clickTargets = collectOnClickTargets(gltf.scene, onClickNodeNames);
    const extraTargets = collectNamedClickTargets(gltf.scene, buttonNames);
    const allTargets = clickTargets.concat(extraTargets.filter(t => !clickTargets.includes(t)));
    if (allTargets.length > 0) {
      if (DEBUG_ONCLICK) {
        log('[onclick] node names:', Array.from(onClickNodeNames));
        log(
          '[onclick] click targets:',
          allTargets.map(t => t?.name || '(unnamed)')
        );
      }
      const raycaster = new Raycaster();
      const pointer = new Vector2();
      const prevCursor = domElement.style.cursor;
      let hoverSet = false;
      const tmpBox = new Box3();
      const tmpVec = new Vector3();
      const updatePointer = event => {
        const rect = domElement.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) return { inside: false };
        pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        return { inside: true };
      };
      const intersectTargetsByBounds = () => {
        const hits = [];
        for (const target of clickTargets) {
          tmpBox.setFromObject(target);
          const point = raycaster.ray.intersectBox(tmpBox, tmpVec);
          if (!point) continue;
          hits.push({
            object: target,
            distance: point.distanceTo(raycaster.ray.origin)
          });
        }
        hits.sort((a, b) => a.distance - b.distance);
        return hits;
      };
      const setHoverCursor = hovering => {
        if (hovering) {
          if (domElement.style.cursor !== 'pointer') {
            domElement.style.cursor = 'pointer';
            hoverSet = true;
          }
        } else if (hoverSet) {
          domElement.style.cursor = prevCursor || 'auto';
          hoverSet = false;
        }
      };
      const onPointerMove = event => {
        const { inside } = updatePointer(event);
        if (!inside) {
          setHoverCursor(false);
          return;
        }
        raycaster.setFromCamera(pointer, camera);
        let hits = raycaster.intersectObjects(allTargets, false);
        if (!hits.length) {
          hits = intersectTargetsByBounds();
        }
        if (!hits.length) {
          hits = raycaster.intersectObject(gltf.scene, true);
        }
        let hasAction = false;
        for (const hit of hits) {
          const actionsToPlay = resolveOnClickActions(
            hit?.object,
            gltf.scene,
            onClickNodeActions,
            onClickActions,
            buttonNames
          );
          if (actionsToPlay.size) {
            hasAction = true;
            break;
          }
        }
        setHoverCursor(hasAction);
      };
      const onPointerDown = event => {
        const { inside } = updatePointer(event);
        if (!inside) return;
        raycaster.setFromCamera(pointer, camera);
        let hits = raycaster.intersectObjects(allTargets, false);
        const hitsTargets = hits.length;
        if (!hits.length) {
          hits = intersectTargetsByBounds();
        }
        if (!hits.length) {
          hits = raycaster.intersectObject(gltf.scene, true);
        }
        if (DEBUG_ONCLICK) {
          log('[onclick] pointerdown hits:', {
            targets: hitsTargets,
            total: hits.length
          });
        }
        if (!hits.length) return;
        for (const hitInfo of hits) {
          const hit = hitInfo?.object;
          const actionsToPlay = resolveOnClickActions(
            hit,
            gltf.scene,
            onClickNodeActions,
            onClickActions,
            buttonNames
          );
          if (DEBUG_ONCLICK) {
            log('[onclick] hit:', hit?.name || '(unnamed)', 'actions:', actionsToPlay.size);
          }
          if (!actionsToPlay.size) continue;
          actionsToPlay.forEach(action => playOnClickAction(action));
          break;
        }
      };
      const onPointerLeave = () => setHoverCursor(false);
      const listenerTarget = typeof window !== 'undefined' ? window : domElement;
      const listenerOptions = { capture: true };
      listenerTarget.addEventListener('pointermove', onPointerMove, listenerOptions);
      listenerTarget.addEventListener('pointerdown', onPointerDown, listenerOptions);
      disposeOnClick = () => {
        listenerTarget.removeEventListener('pointermove', onPointerMove, listenerOptions);
        listenerTarget.removeEventListener('pointerdown', onPointerDown, listenerOptions);
        domElement.removeEventListener('pointerleave', onPointerLeave);
        setHoverCursor(false);
      };
      domElement.addEventListener('pointerleave', onPointerLeave);
    }
  } else if (DEBUG_ONCLICK) {
    log('[onclick] skipped wiring', {
      onClickNodeNames: Array.from(onClickNodeNames),
      hasCamera: Boolean(camera),
      hasDomElement: Boolean(domElement)
    });
  }

  mixer.addEventListener('finished', e => {
    if (clickOnceActions.has(e.action)) {
      armOnClickAction(e.action);
      return;
    }
    if (onceActions.has(e.action)) {
      e.action.paused = true;
      e.action.time = e.action.getClip().duration;
    }
  });

  return {
    mixer,
    actions,
    update: dt => mixer?.update(dt),
    dispose: () => {
      disposeOnClick();
      mixer?.stopAllAction();
    }
  };
}
